import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, ArrowLeft, CheckCircle2, ShieldCheck, Thermometer, Wind, Wrench, Sparkles, Clock, ArrowRight, Zap, Apple, Send } from 'lucide-react';
import Footer from '../components/Footer';
import HeaderSocials from '../components/HeaderSocials';
import InstagramVideoSection from '../components/InstagramVideoSection';
import LandingFaqSchema from '../components/LandingFaqSchema';
import useLandingContent from '../hooks/useLandingContent';

const MACBOOKS = {
  air: {
    label: 'MacBook Air',
    short: 'Air',
    hero: '/images/generated/macbook-hero.png',
    price: 1200,
    duration: '2–3 год',
    warranty: '30 днів',
    accent: 'from-sky-300 to-blue-500',
    glow: 'shadow-[0_0_80px_rgba(56,189,248,0.4)]',
    bgGlow: 'rgba(56,189,248,0.22)',
    features: [
      'Розбирання зі зняттям нижньої кришки (pentalobe)',
      'Демонтаж системи охолодження (для моделей з кулером)',
      'Чистка радіатора, кулера та вентиляційних решіток',
      'Заміна термопасти на чип Apple Silicon / Intel',
      'Заміна термопрокладок за необхідності',
      'Профілактика портів та динаміків від пилу'
    ]
  },
  pro13: {
    label: 'MacBook Pro 13"',
    short: 'Pro 13"',
    hero: '/images/generated/macbook-hero.png',
    price: 1500,
    duration: '2.5–3.5 год',
    warranty: '30 днів',
    accent: 'from-slate-300 to-sky-500',
    glow: 'shadow-[0_0_80px_rgba(100,116,139,0.4)]',
    bgGlow: 'rgba(148,163,184,0.22)',
    features: [
      'Повне розбирання корпусу з демонтажем плати',
      'Чистка двох кулерів, радіатора та теплових трубок',
      'Заміна термопасти преміум-класу (Thermal Grizzly / MX-6)',
      'Заміна термопрокладок VRM за необхідності',
      'Чистка від пилу системи охолодження та портів',
      'Тестування температур під навантаженням'
    ]
  },
  pro14: {
    label: 'MacBook Pro 14"',
    short: 'Pro 14"',
    hero: '/images/generated/macbook-hero.png',
    price: 1700,
    duration: '3–4 год',
    warranty: '30 днів',
    accent: 'from-sky-400 to-indigo-500',
    glow: 'shadow-[0_0_80px_rgba(59,130,246,0.4)]',
    bgGlow: 'rgba(59,130,246,0.22)',
    features: [
      'Повне розбирання корпусу M1 Pro / M2 Pro / M3 Pro',
      'Чистка подвійної системи охолодження від пилу',
      'Заміна термопасти на чип Apple Silicon',
      'Заміна термопрокладок за необхідності',
      'Профілактика акумулятора та портів',
      'Тестування продуктивності та температур'
    ]
  },
  pro16: {
    label: 'MacBook Pro 16"',
    short: 'Pro 16"',
    hero: '/images/generated/macbook-hero.png',
    price: 1900,
    duration: '3–4 год',
    warranty: '30 днів',
    accent: 'from-blue-400 to-indigo-600',
    glow: 'shadow-[0_0_80px_rgba(79,70,229,0.4)]',
    bgGlow: 'rgba(99,102,241,0.22)',
    features: [
      'Повне розбирання топової моделі Pro 16"',
      'Чистка масивної системи охолодження та теплотрубок',
      'Заміна термопасти на потужний чип M-серії / Intel',
      'Заміна термопрокладок VRM та VRAM',
      'Профілактика акумулятора великої ємності',
      'Тестування під максимальним навантаженням'
    ]
  }
};

const STEPS = [
  { title: 'Безкоштовна діагностика', desc: 'Заміряємо температури, слухаємо кулер, оглядаємо MacBook. Даємо чесний висновок — чи потрібна чистка та профілактика.', img: '/images/generated/macbook-step-1.png', icon: ShieldCheck },
  { title: 'Розбирання', desc: 'Знімаємо нижню кришку спеціальними pentalobe-викрутками, від\'єднуємо акумулятор. Все — в антистатичних умовах.', img: '/images/generated/macbook-step-2.png', icon: Wrench },
  { title: 'Показуємо що всередині', desc: 'Фото «до» надсилаємо у Telegram. Ви побачите весь пил, який забиває кулер і заважає охолодженню Apple Silicon.', img: '/images/generated/macbook-step-3.png', icon: Sparkles },
  { title: 'Глибока чистка', desc: 'Стисненим повітрям, м\'якими щітками і спеціальними розчинами видаляємо пил з радіатора, кулера та решіток.', img: '/images/generated/macbook-step-4.png', icon: Wind },
  { title: 'Заміна термопасти', desc: 'Наносимо преміум-термопасту на чип. Знову збираємо, підключаємо акумулятор та шлейфи.', img: '/images/generated/macbook-step-5.png', icon: Thermometer },
  { title: 'Тестування', desc: 'Прогріваємо під навантаженням, стежимо за температурами та throttling. Показуємо результат «до і після».', img: '/images/generated/macbook-step-1.png', icon: Zap }
];

const SIGNS = [
  { icon: '🔥', text: 'Корпус MacBook гарячий навіть у простих задачах' },
  { icon: '🌀', text: 'Кулер гуде на максимумі без причини' },
  { icon: '🐢', text: 'Throttling: падає продуктивність під навантаженням' },
  { icon: '🎬', text: 'Тормозить рендер, монтаж, важкі програми' },
  { icon: '🔋', text: 'Швидко сідає батарея, MacBook перегрівається' },
  { icon: '📅', text: 'Не робили профілактику більше 1.5–2 років' }
];

const FAQS = [
  { q: 'Чи безпечно розбирати MacBook?', a: 'Так. Наш майстер спеціалізується на техніці Apple, використовує оригінальні pentalobe-інструменти та антистатичне обладнання. За весь час — жодного пошкодженого пристрою.' },
  { q: 'Скільки триває чистка MacBook?', a: 'MacBook Air — 2–3 години, Pro 13"/14"/16" — 3–4 години. Можна зачекати або залишити з ранку і забрати ввечері.' },
  { q: 'Яку термопасту ви використовуєте?', a: 'Преміум-класу — Thermal Grizzly Kryonaut або Arctic MX-6. Вони тримають температуру значно краще за стокову пасту Apple, яка з часом висихає.' },
  { q: 'Мої дані та файли в безпеці?', a: 'Абсолютно. Ми працюємо лише із «залізом» — накопичувач, дані та macOS не чіпаємо. Все залишається на місці.' },
  { q: 'Даєте гарантію?', a: 'Так, офіційна гарантія 30 днів на виконані роботи. Діагностика — безкоштовна.' }
];

const MacBookCleaningPage = () => {
  const [active, setActive] = useState('air');
  const landing = useLandingContent('macbook');
  const c = MACBOOKS[active];
  const grad = `bg-gradient-to-r ${c.accent} bg-clip-text text-transparent`;

  return (
    <div className="min-h-screen console-page" data-testid="macbook-cleaning-page">
      <Helmet>
        <title>Чистка MacBook в Одесі — Air, Pro 13/14/16 | CIMM Computers</title>
        <meta name="description" content="Професійна чистка MacBook Air та MacBook Pro 13/14/16 в Одесі. Заміна термопасти, чистка системи охолодження, усунення throttling та перегріву. MacBook Air від 1200 грн, Pro від 1500 грн. Гарантія 30 днів. ☎ +38 097 417 8788" />
        <meta name="keywords" content="чистка MacBook Одеса, чистка макбук Одеса, заміна термопасти MacBook, чистка MacBook Air, чистка MacBook Pro, перегрів MacBook Одеса, обслуговування MacBook" />
        <link rel="canonical" href="https://cimm.od.ua/chistka-macbook" />
        <meta property="og:title" content="Чистка MacBook в Одесі — Air / Pro | CIMM Computers" />
        <meta property="og:description" content="Чистка та заміна термопасти MacBook Air і Pro. Усуваємо перегрів і throttling. Гарантія 30 днів." />
        <meta property="og:image" content="https://cimm.od.ua/images/generated/macbook-hero.png" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cimm.od.ua/chistka-macbook" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: 'Чистка MacBook та заміна термопасти',
          provider: {
            '@type': 'LocalBusiness',
            name: 'CIMM Computers',
            telephone: '+380974178788',
            address: { '@type': 'PostalAddress', addressLocality: 'Одеса', addressCountry: 'UA' }
          },
          areaServed: { '@type': 'City', name: 'Одеса' },
          offers: [
            { '@type': 'Offer', name: 'Чистка MacBook Air', price: '1200', priceCurrency: 'UAH' },
            { '@type': 'Offer', name: 'Чистка MacBook Pro 13"', price: '1500', priceCurrency: 'UAH' },
            { '@type': 'Offer', name: 'Чистка MacBook Pro 14"', price: '1700', priceCurrency: 'UAH' },
            { '@type': 'Offer', name: 'Чистка MacBook Pro 16"', price: '1900', priceCurrency: 'UAH' }
          ]
        })}</script>
      </Helmet>
      <LandingFaqSchema category="macbook" />

      {/* Header */}
      <header className="cyber-header fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="header-home-link">
            <div className="text-2xl font-bold neon-text">CIMM</div>
            <div className="text-sm text-gray-400 hidden md:block">Комп'ютери та Ремонт</div>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-gray-300 hover:text-sky-400 transition-all">Головна</Link>
            <Link to="/phones" className="text-gray-300 hover:text-sky-400 transition-all">Телефони</Link>
            <Link to="/laptops" className="text-gray-300 hover:text-sky-400 transition-all">Ноутбуки</Link>
            <Link to="/chistka-noutbukiv" className="text-gray-300 hover:text-sky-400 transition-all">Чистка ноутбуків</Link>
            <Link to="/contacts" className="text-gray-300 hover:text-sky-400 transition-all">Контакти</Link>
          </nav>
          <div className="flex items-center gap-2">
            <HeaderSocials />
            <a href="tel:+380974178788" className="neon-text font-semibold" data-testid="header-phone-link">+38 097 417 8788</a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[92vh] md:min-h-[88vh] flex items-center overflow-hidden pt-28 pb-16 md:pt-24 md:pb-20 console-hero">
        <div className="absolute inset-0 pointer-events-none console-hero-bg" aria-hidden="true">
          <div className="absolute inset-0 transition-all duration-700" style={{ background: `radial-gradient(ellipse 70% 90% at 75% 50%, ${c.bgGlow}, transparent 65%)` }} />
        </div>

        <div className="absolute inset-y-0 right-0 w-full md:w-[62%] lg:w-[58%] pointer-events-none overflow-hidden">
          <img src={c.hero} alt={c.label} className="w-full h-full object-contain object-right transition-all duration-700" data-testid="macbook-hero-img" />
          <div className="absolute inset-y-0 left-0 w-1/2 console-hero-fade" />
        </div>

        <div className="container mx-auto max-w-7xl px-4 relative z-10">
          <div className="flex flex-col items-center mb-10">
            <Link to="/" className="self-start inline-flex items-center gap-2 hero-back-link mb-6 transition-colors text-sm" data-testid="back-home-link">
              <ArrowLeft className="w-4 h-4" /> На головну
            </Link>

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-500/10 border border-sky-500/30 mb-5 backdrop-blur-sm">
              <Apple className="w-3.5 h-3.5 text-sky-400" />
              <span className="text-xs uppercase tracking-widest text-sky-400">Чистка MacBook · Одеса</span>
            </div>

            <div className="inline-flex flex-wrap justify-center p-1.5 rounded-full console-switcher-bg backdrop-blur-xl border shadow-2xl" data-testid="macbook-switcher">
              {Object.entries(MACBOOKS).map(([key, m]) => (
                <button
                  key={key}
                  onClick={() => setActive(key)}
                  data-testid={`switcher-${key}`}
                  className={`px-4 md:px-6 py-2.5 rounded-full text-sm md:text-base font-semibold transition-all duration-500 ${
                    active === key ? `bg-gradient-to-r ${m.accent} text-white shadow-lg` : 'hero-switcher-idle hover:opacity-100'
                  }`}
                >
                  {m.short}
                </button>
              ))}
            </div>
          </div>

          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.02] mb-5 hero-h1">
              Чистка <span className={`${grad} whitespace-nowrap`}>MacBook {c.short}</span>
              <br />в Одесі
            </h1>
            <p className="text-lg md:text-xl mb-6 max-w-xl hero-lead">
              Ваш MacBook перестане перегріватися та тротлити. Прибираємо throttling, знижуємо температури й повертаємо тиху роботу. Термін від <span className="font-bold hero-lead-strong">{c.duration}</span>. Гарантія <span className="font-bold hero-lead-strong">{c.warranty}</span>.
            </p>

            <div className="flex flex-wrap items-baseline gap-3 mb-8">
              <div className="hero-price-from text-sm">від</div>
              <div className={`text-6xl md:text-7xl font-black ${grad}`}>{c.price}</div>
              <div className="hero-price-unit text-2xl font-bold">грн</div>
            </div>

            <div className="flex flex-wrap gap-3">
              <a href="tel:+380974178788" data-testid="hero-cta-call" className={`inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r ${c.accent} text-white font-bold rounded-full transition-all duration-300 hover:scale-105 ${c.glow}`}>
                <Phone className="w-5 h-5" /> Записатись зараз
              </a>
              <a href="#steps" data-testid="hero-cta-details" className="inline-flex items-center gap-2 px-8 py-4 hero-cta-secondary font-semibold rounded-full border transition-all">
                Як ми це робимо <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="flex flex-wrap gap-6 mt-8 text-sm hero-meta">
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-sky-400" /> {c.duration}</div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-sky-400" /> Гарантія {c.warranty}</div>
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-sky-400" /> Діагностика 0 грн</div>
            </div>
          </div>

          <div className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 z-10 hidden lg:block">
            <div className="glass-card px-5 py-4 backdrop-blur-2xl border text-center rotate-3 hero-effect-badge">
              <div className="text-[10px] uppercase tracking-widest hero-effect-label">Ефект</div>
              <div className={`text-4xl font-black ${grad}`}>−20°C</div>
              <div className="text-xs hero-effect-sub mt-1">без throttling</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="glass-card p-6 md:p-10 backdrop-blur-2xl bg-white/[0.03] border border-white/10">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-1">
                <div className="text-xs uppercase tracking-widest text-sky-400 mb-2">Що входить</div>
                <h3 className="text-2xl md:text-3xl font-black text-white leading-tight">Повне обслуговування {c.label}</h3>
                <p className="text-gray-400 text-sm mt-3">Все включено у ціну. Ніяких прихованих доплат.</p>
              </div>
              <ul className="md:col-span-2 grid sm:grid-cols-2 gap-3">
                {c.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-sky-400" />
                    <span className="text-sm md:text-base">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Signs */}
      <section className="py-14 md:py-20 px-4 relative overflow-hidden">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-widest text-sky-400 mb-2">Симптоми</div>
            <h2 className="text-3xl md:text-5xl font-black text-white">Коли MacBook пора на <span className={grad}>чистку</span></h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {SIGNS.map((s, i) => (
              <div key={i} className="glass-card p-5 hover:border-sky-500/50 transition-all" data-testid={`sign-${i}`}>
                <div className="text-3xl mb-2">{s.icon}</div>
                <p className="text-gray-300 text-sm md:text-base">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section id="steps" className="py-14 md:py-24 px-4 relative">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-widest text-sky-400 mb-2">Процес</div>
            <h2 className="text-3xl md:text-5xl font-black text-white mb-4">6 етапів <span className={grad}>ідеальної чистки</span></h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Прозоро від першого дзвінка до тестування. Ми фотографуємо кожен етап і надсилаємо вам у Telegram.</p>
          </div>

          <div className="space-y-8 md:space-y-16">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const reverse = i % 2 === 1;
              return (
                <div key={i} className={`grid md:grid-cols-2 gap-6 md:gap-10 items-center ${reverse ? 'md:[direction:rtl]' : ''}`} data-testid={`step-${i}`}>
                  <div className="md:[direction:ltr]">
                    <div className="rounded-2xl overflow-hidden border border-white/10 relative aspect-[4/3] bg-black/40">
                      <img src={s.img} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute top-3 left-3 w-10 h-10 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-sky-400 font-black text-lg border border-sky-500/40">
                        {String(i + 1).padStart(2, '0')}
                      </div>
                    </div>
                  </div>
                  <div className="md:[direction:ltr]">
                    <div className={`inline-flex items-center gap-2 mb-3 px-3 py-1.5 rounded-full bg-gradient-to-r ${c.accent}`}>
                      <Icon className="w-4 h-4 text-white" />
                      <span className="text-xs uppercase tracking-wider text-white font-semibold">Крок {i + 1}</span>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-black text-white mb-3">{s.title}</h3>
                    <p className="text-gray-300 text-base md:text-lg leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Price + trust */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-6xl grid md:grid-cols-2 gap-8 items-stretch">
          <div className="glass-card p-8 flex flex-col justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-sky-400 mb-3">Прайс</div>
              <h3 className="text-3xl font-black text-white mb-6">Ціна на чистку {c.label}</h3>
              <div className="flex items-baseline gap-3 mb-6">
                <div className="text-gray-400">від</div>
                <div className={`text-7xl font-black ${grad}`}>{c.price}</div>
                <div className="text-gray-400 text-2xl">грн</div>
              </div>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-sky-400 flex-shrink-0 mt-0.5" /> Термопаста преміум-класу входить у ціну</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-sky-400 flex-shrink-0 mt-0.5" /> Термопрокладки — за необхідності</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-sky-400 flex-shrink-0 mt-0.5" /> Діагностика та тестування — 0 грн</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-sky-400 flex-shrink-0 mt-0.5" /> Гарантія на послугу — {c.warranty}</li>
              </ul>
            </div>
            <a href="tel:+380974178788" data-testid="price-cta-call" className={`mt-8 inline-flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r ${c.accent} text-white font-bold rounded-full text-lg ${c.glow}`}>
              <Phone className="w-5 h-5" /> Записатись за 30 сек
            </a>
          </div>
          <div className="glass-card p-8 space-y-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-sky-400 mb-3">Чому нас</div>
              <h3 className="text-3xl font-black text-white">Обирають в Одесі</h3>
            </div>
            <div className="space-y-4">
              {[
                { n: '8+', l: 'років ремонтуємо техніку Apple' },
                { n: '1500+', l: 'MacBook пройшло через наші руки' },
                { n: '−20°C', l: 'середнє зниження температури' },
                { n: '30 днів', l: 'офіційної гарантії' }
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className={`text-3xl md:text-4xl font-black ${grad} min-w-[100px]`}>{s.n}</div>
                  <div className="text-gray-300">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <InstagramVideoSection
        url={landing?.instagram_video}
        accentClass="text-sky-400"
      />

      {/* FAQ */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-widest text-sky-400 mb-2">FAQ</div>
            <h2 className="text-3xl md:text-5xl font-black text-white">Часті питання</h2>
          </div>
          <div className="space-y-3" data-testid="faq-section">
            {FAQS.map((f, i) => (
              <details key={i} className="glass-card p-6 group cursor-pointer" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between text-white font-semibold list-none">
                  <span className="pr-4">{f.q}</span>
                  <span className="text-sky-400 text-2xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-gray-400 mt-4 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 md:py-24 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="glass-card p-8 md:p-14 text-center relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4">Ваш MacBook заслуговує <span className={grad}>тиші</span></h2>
              <p className="text-gray-300 text-lg mb-8 max-w-xl mx-auto">Один дзвінок — і вже сьогодні ви заберете MacBook, який працює прохолодно й тихо.</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <a href="tel:+380974178788" data-testid="final-cta-call" className={`inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r ${c.accent} text-white font-bold rounded-full text-lg ${c.glow}`}>
                  <Phone className="w-5 h-5" /> +38 097 417 8788
                </a>
                <a href="https://t.me/cimmodua" target="_blank" rel="noopener noreferrer" data-testid="final-cta-telegram" className="inline-flex items-center gap-2 px-10 py-4 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-full border border-white/10 transition-all">
                  <Send className="w-5 h-5" /> Написати в Telegram
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MacBookCleaningPage;
